import { bookmarkInputSchema, type Bookmark, type BookmarkInput } from "@learn-flow/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, MoreHorizontal, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ApiError } from "../../lib/api.js";
import {
  bookmarksQueryKey,
  bookmarksQueryOptions,
  createBookmark,
  deleteBookmark,
  updateBookmark,
} from "./bookmark-api.js";

type DialogState = { mode: "create" } | { mode: "edit"; bookmark: Bookmark } | null;

export function BookmarkSection(): React.JSX.Element {
  const query = useQuery(bookmarksQueryOptions);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<Bookmark | null>(null);
  const [openMenu, setOpenMenu] = useState<{
    bookmark: Bookmark;
    top: number;
    right: number;
  } | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const actionButtons = useRef(new Map<string, HTMLButtonElement>());
  const dialogOpener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const desktopMedia = window.matchMedia?.("(min-width: 981px)") ?? null;
    const sidebar = addButton.current?.closest("aside") ?? null;
    const dismissUnavailableOverlays = (): void => {
      if ((desktopMedia?.matches ?? true) && sidebar && !sidebar.hasAttribute("hidden")) return;
      setOpenMenu(null);
      setDialogState(null);
      setDeleteTarget(null);
    };
    const observer = sidebar ? new MutationObserver(dismissUnavailableOverlays) : null;
    if (observer && sidebar) {
      observer.observe(sidebar, { attributes: true, attributeFilter: ["hidden"] });
    }
    desktopMedia?.addEventListener("change", dismissUnavailableOverlays);
    dismissUnavailableOverlays();
    return () => {
      observer?.disconnect();
      desktopMedia?.removeEventListener("change", dismissUnavailableOverlays);
    };
  }, []);

  const openCreateDialog = (): void => {
    dialogOpener.current = addButton.current;
    setDialogState({ mode: "create" });
  };

  const openActions = (bookmark: Bookmark, button: HTMLButtonElement): void => {
    if (openMenu?.bookmark.id === bookmark.id) {
      setOpenMenu(null);
      return;
    }
    const bounds = button.getBoundingClientRect();
    setOpenMenu({
      bookmark,
      top: Math.min(bounds.bottom + 5, window.innerHeight - 92),
      right: Math.max(8, window.innerWidth - bounds.right),
    });
  };

  return (
    <section className="bookmark-section" aria-labelledby="bookmarks-title">
      <div className="bookmark-heading">
        <h2 id="bookmarks-title">Bookmarks</h2>
        <button ref={addButton} type="button" aria-label="Add bookmark" onClick={openCreateDialog}>
          <Plus size={17} aria-hidden="true" />
        </button>
      </div>

      <div className="bookmark-list" data-testid="bookmark-list">
        {query.isPending ? <p className="bookmark-status">Loading bookmarks…</p> : null}
        {query.isError ? (
          <div className="bookmark-status" role="alert">
            <p>Bookmarks could not be loaded.</p>
            <button type="button" onClick={() => void query.refetch()}>
              Try again
            </button>
          </div>
        ) : null}
        {query.data?.bookmarks.length === 0 ? (
          <p className="bookmark-status">No bookmarks yet</p>
        ) : null}
        {query.data?.bookmarks.map((bookmark, index) => (
          <div className="bookmark-row" key={bookmark.id}>
            <ExternalLink size={15} aria-hidden="true" />
            <a
              href={bookmark.link}
              target="_blank"
              rel="noopener noreferrer"
              title={bookmark.label}
            >
              {bookmark.label}
            </a>
            <button
              ref={(element) => {
                if (element) actionButtons.current.set(bookmark.id, element);
                else actionButtons.current.delete(bookmark.id);
              }}
              type="button"
              aria-label={`Actions for ${bookmark.label}, bookmark ${(index + 1).toString()}`}
              aria-haspopup="menu"
              aria-expanded={openMenu?.bookmark.id === bookmark.id}
              onClick={(event) => openActions(bookmark, event.currentTarget)}
            >
              <MoreHorizontal size={17} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>

      {openMenu
        ? createPortal(
            <BookmarkMenu
              menu={openMenu}
              opener={actionButtons.current.get(openMenu.bookmark.id) ?? null}
              onClose={() => setOpenMenu(null)}
              onEdit={() => {
                dialogOpener.current = actionButtons.current.get(openMenu.bookmark.id) ?? null;
                setOpenMenu(null);
                setDialogState({ mode: "edit", bookmark: openMenu.bookmark });
              }}
              onDelete={() => {
                dialogOpener.current = actionButtons.current.get(openMenu.bookmark.id) ?? null;
                setOpenMenu(null);
                setDeleteTarget(openMenu.bookmark);
              }}
            />,
            document.body,
          )
        : null}

      {dialogState ? (
        <BookmarkFormDialog
          state={dialogState}
          opener={dialogOpener}
          onDismiss={() => setDialogState(null)}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteBookmarkDialog
          bookmark={deleteTarget}
          opener={dialogOpener}
          successFocus={() => {
            const bookmarks = query.data?.bookmarks ?? [];
            const deletedIndex = bookmarks.findIndex(({ id }) => id === deleteTarget.id);
            const neighbor = bookmarks[deletedIndex + 1] ?? bookmarks[deletedIndex - 1];
            return (neighbor ? actionButtons.current.get(neighbor.id) : null) ?? addButton.current;
          }}
          onDismiss={() => setDeleteTarget(null)}
        />
      ) : null}
    </section>
  );
}

function BookmarkMenu({
  menu,
  opener,
  onClose,
  onEdit,
  onDelete,
}: {
  menu: { bookmark: Bookmark; top: number; right: number };
  opener: HTMLButtonElement | null;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const closeFromOutside = (event: PointerEvent): void => {
      if (
        menuRef.current?.contains(event.target as Node) ||
        opener?.contains(event.target as Node)
      ) {
        return;
      }
      const shouldRestoreFocus = !isMeaningfulInteractiveTarget(event.target);
      onClose();
      if (shouldRestoreFocus) {
        window.requestAnimationFrame(() => focusIfVisible(opener));
      }
    };
    const closeFromEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
      focusIfVisible(opener);
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromEscape);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromEscape);
    };
  }, [onClose, opener]);

  return (
    <div
      ref={menuRef}
      className="bookmark-menu"
      role="menu"
      aria-label={`Actions for ${menu.bookmark.label}`}
      style={{ top: menu.top, right: menu.right }}
    >
      <button type="button" role="menuitem" onClick={onEdit}>
        Edit bookmark
      </button>
      <button type="button" className="bookmark-menu-delete" role="menuitem" onClick={onDelete}>
        Delete bookmark
      </button>
    </div>
  );
}

function BookmarkFormDialog({
  state,
  opener,
  onDismiss,
}: {
  state: Exclude<DialogState, null>;
  opener: React.RefObject<HTMLElement | null>;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const labelInput = useRef<HTMLInputElement>(null);
  const linkInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(state.mode === "edit" ? state.bookmark.label : "");
  const [link, setLink] = useState(state.mode === "edit" ? state.bookmark.link : "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState("");
  const mutation = useMutation({
    mutationFn: (input: BookmarkInput) =>
      state.mode === "create" ? createBookmark(input) : updateBookmark(state.bookmark.id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookmarksQueryKey });
      dialog.current?.close();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem?.fieldErrors) {
        const serverErrors = Object.fromEntries(
          Object.entries(error.problem.fieldErrors).map(([field, messages]) => [
            field,
            messages[0] ?? "Check this value.",
          ]),
        );
        setErrors(serverErrors);
      }
      setSaveError("The bookmark could not be saved. Your changes are still here; try again.");
    },
  });
  useBookmarkDialog(dialog, onDismiss, opener, mutation.isPending);

  const submit = (): void => {
    const parsed = bookmarkInputSchema.safeParse({ label, link });
    if (!parsed.success) {
      const nextErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]?.toString() ?? "label";
        nextErrors[field] ??= issue.message;
      }
      setErrors(nextErrors);
      setSaveError("");
      window.requestAnimationFrame(() =>
        (nextErrors["label"] ? labelInput.current : linkInput.current)?.focus(),
      );
      return;
    }
    setErrors({});
    setSaveError("");
    mutation.mutate(parsed.data);
  };

  const title = state.mode === "create" ? "Add bookmark" : "Edit bookmark";
  return (
    <dialog ref={dialog} className="calm-dialog bookmark-dialog" aria-labelledby="bookmark-title">
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!mutation.isPending) submit();
        }}
      >
        <span className="section-kicker">Bookmark</span>
        <h2 id="bookmark-title">{title}</h2>
        <label>
          Label
          <input
            ref={labelInput}
            autoFocus
            value={label}
            maxLength={160}
            aria-invalid={Boolean(errors["label"])}
            aria-describedby={errors["label"] ? "bookmark-label-error" : undefined}
            onChange={(event) => {
              setLabel(event.target.value);
              setErrors((current) => ({ ...current, label: "" }));
            }}
          />
        </label>
        {errors["label"] ? (
          <p id="bookmark-label-error" className="field-error">
            {errors["label"]}
          </p>
        ) : null}
        <label>
          Link
          <input
            ref={linkInput}
            type="url"
            value={link}
            maxLength={2_048}
            aria-invalid={Boolean(errors["link"])}
            aria-describedby={errors["link"] ? "bookmark-link-error" : undefined}
            onChange={(event) => {
              setLink(event.target.value);
              setErrors((current) => ({ ...current, link: "" }));
            }}
          />
        </label>
        {errors["link"] ? (
          <p id="bookmark-link-error" className="field-error">
            {errors["link"]}
          </p>
        ) : null}
        <div className="dialog-message" aria-live="polite">
          {saveError ? (
            <p className="field-error" role="alert">
              {saveError}
            </p>
          ) : null}
        </div>
        <div className="dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={mutation.isPending}
            onClick={() => dialog.current?.close()}
          >
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save bookmark"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function DeleteBookmarkDialog({
  bookmark,
  opener,
  successFocus,
  onDismiss,
}: {
  bookmark: Bookmark;
  opener: React.RefObject<HTMLElement | null>;
  successFocus: () => HTMLElement | null;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteBookmark(bookmark.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bookmarksQueryKey });
      opener.current = successFocus();
      dialog.current?.close();
    },
  });
  useBookmarkDialog(dialog, onDismiss, opener, mutation.isPending);

  return (
    <dialog
      ref={dialog}
      className="calm-dialog bookmark-dialog"
      aria-labelledby="delete-bookmark-title"
    >
      <div>
        <span className="section-kicker">Delete bookmark</span>
        <h2 id="delete-bookmark-title">Delete “{bookmark.label}”?</h2>
        <p>This bookmark will be permanently deleted. This cannot be undone.</p>
        {mutation.isError ? (
          <p className="field-error" role="alert">
            The bookmark could not be deleted. It is still in your list; try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={mutation.isPending}
            onClick={() => dialog.current?.close()}
          >
            Keep bookmark
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Deleting…" : "Delete bookmark"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function useBookmarkDialog(
  reference: React.RefObject<HTMLDialogElement | null>,
  onDismiss: () => void,
  opener: React.RefObject<HTMLElement | null>,
  pending: boolean,
): void {
  const dismiss = useRef(onDismiss);
  const pendingState = useRef(pending);
  dismiss.current = onDismiss;
  pendingState.current = pending;
  useEffect(() => {
    const dialog = reference.current;
    if (!dialog) return;
    const close = (): void => {
      dismiss.current();
      window.requestAnimationFrame(() => focusIfVisible(opener.current));
    };
    const cancel = (event: Event): void => {
      if (pendingState.current) event.preventDefault();
    };
    const trapFocus = (event: KeyboardEvent): void => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)"),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener("close", close);
    dialog.addEventListener("cancel", cancel);
    dialog.addEventListener("keydown", trapFocus);
    if (!dialog.open) dialog.showModal();
    return () => {
      dialog.removeEventListener("close", close);
      dialog.removeEventListener("cancel", cancel);
      dialog.removeEventListener("keydown", trapFocus);
    };
  }, [opener, reference]);
}

function focusIfVisible(target: HTMLElement | null): void {
  if (!target?.isConnected || target.closest("[hidden]")) return;
  for (let current: HTMLElement | null = target; current; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return;
  }
  target.focus();
}

function isMeaningfulInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), label, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"]), [role="button"], [role="link"], [role="menuitem"]',
    ),
  );
}
